import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { CaseStrip } from "@/components/CasePreview";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideLinks } from "@/components/GuideLinks";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-entwickler";
const TITLE = "Shopify Entwickler buchen | 110 € netto pro Stunde";
const DESCRIPTION =
  "Shopify Entwickler und Shopify Experte für Liquid, Sections, Schnittstellen und Fehlerbehebung. 110 € netto pro Stunde, direkt mit dem, der umsetzt. DACH-weit remote.";

const faqs = [
  {
    q: "Was kostet ein Shopify Entwickler?",
    a: "Bei mir 110 € netto pro Stunde. Vor dem Start schätze ich den Aufwand für Ihre Aufgabe, bei größeren Umbauten vereinbaren wir einen Festpreis.",
  },
  {
    q: "Was ist der Unterschied zwischen Freelancer und Agentur?",
    a: "Sie sprechen direkt mit dem, der die Arbeit macht. Es gibt keine Projektleitung dazwischen, kein Weiterreichen an wechselnde Entwickler und keinen Mindestumfang, damit sich ein Projekt lohnt.",
  },
  {
    q: "Welche Aufgaben übernehmen Sie?",
    a: "Theme- und Liquid-Entwicklung, eigene Sections, Varianten- und Preislogik, Anbindung von Apps und Schnittstellen, Datenimporte, Fehlerbehebung und Ladezeit. Was ich nicht sinnvoll umsetzen kann, sage ich Ihnen.",
  },
  {
    q: "Arbeiten Sie auch an bestehenden Shops von Vorgängern?",
    a: "Ja, das ist häufig der Fall. Ich sehe mir den vorhandenen Code an und sage Ihnen, ob Reparatur oder Neuaufbau der günstigere Weg ist.",
  },
  {
    q: "Wie läuft die Zusammenarbeit ab?",
    a: "Sie schildern die Aufgabe, ich melde mich innerhalb von 48 Stunden mit Einschätzung und Aufwand. Gearbeitet wird in einer Kopie Ihres Themes oder in einem Testshop, veröffentlicht wird erst nach Ihrer Freigabe.",
  },
  {
    q: "Arbeiten Sie remote oder vor Ort?",
    a: "Remote für Kunden in Deutschland, Österreich und der Schweiz. Abstimmung per E-Mail oder Call, je nachdem, was Ihnen lieber ist.",
  },
];

export const Route = createFileRoute("/shopify-entwickler")({
  component: DevPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Shopify Entwickler",
          description: DESCRIPTION,
          serviceType: "Shopify Entwicklung",
          areaServed: ["DE", "AT", "CH"],
          provider: { "@type": "Person", name: "Nicolai Schnepf", url: SITE },
          offers: {
            "@type": "Offer",
            price: "110",
            priceCurrency: "EUR",
            description: "110 € netto pro Stunde, Aufwand vor Start geschätzt.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

const tasks = [
  {
    title: "Theme- und Liquid-Entwicklung",
    text: "Eigene Sections, angepasste Templates und Einstellungen, die Sie danach selbst im Customizer bedienen. Sauber getrennt vom Standard-Theme.",
  },
  {
    title: "Funktionen nach Maß",
    text: "Konfiguratoren, Bundles, Mengenstaffeln, B2B-Preise, Filter oder Rechner. Erst klären wir, ob es eine App schon löst, sonst baue ich es.",
  },
  {
    title: "Schnittstellen und Daten",
    text: "Anbindung an Warenwirtschaft, Versanddienstleister oder Newsletter, Produktimporte und Datenpflege über die Shopify APIs.",
  },
  {
    title: "Fehler und Altlasten",
    text: "Reste alter Apps, doppelte Skripte, kaputte Weiterleitungen, Darstellungsfehler nach Updates. Erst Ursache finden, dann reparieren.",
  },
  {
    title: "Ladezeit und Technik",
    text: "Bilder, Skripte und Apps prüfen, unnötige Last entfernen und messen, was sich davon tatsächlich auf die Ladezeit auswirkt.",
  },
  {
    title: "Laufende Betreuung",
    text: "Viele buchen ein paar Stunden im Monat für alles, was so aufläuft. Ohne Vertragsbindung, Abrechnung nach tatsächlichem Aufwand.",
  },
];

function DevPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main id="top">
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Shopify Entwickler · Nicolai Schnepf
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                Shopify Entwickler, der{" "}
                <span className="text-signal">selbst umsetzt</span>.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                Sie brauchen jemanden, der in Ihrem Shopify-Shop wirklich entwickelt: Liquid
                und Sections, eigene Funktionen, Schnittstellen und Fehler, die niemand
                findet. Sie sprechen dabei direkt mit mir, nicht mit einer Projektleitung.
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                110 € netto pro Stunde. Größere Vorhaben zum Festpreis, Aufwand vor dem
                Start geschätzt.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Kundenprojekte ansehen
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Unverbindlich · Antwort innerhalb von 48 Stunden
              </p>
            </div>
            <div id="anfrage">
              <LeadForm
                source="lp-shopify-entwickler"
                showServices
                defaultService="Anpassungen & Entwicklung"
                headline="Aufgabe schildern"
                messagePlaceholder="Welche Entwicklungsaufgabe steht an, welches Theme nutzen Sie?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Typische Aufgaben</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {tasks.map((s) => (
                <div key={s.title} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Typische Aufgaben und was sie kosten</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Damit Sie vor der Anfrage eine Größenordnung haben: das sind Erfahrungswerte
              aus echten Projekten. Ihren konkreten Aufwand schätze ich vor dem Start.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { task: "Eigene Section oder Funktion", time: "2 bis 6 Stunden", cost: "220 bis 660 €", note: "Liquid-Entwicklung, die Sie danach selbst im Customizer bedienen, sauber getrennt vom Standard-Theme." },
                { task: "Schnittstelle anbinden", time: "4 bis 10 Stunden", cost: "440 bis 1.100 €", note: "Warenwirtschaft, DATEV, Versanddienstleister oder Newsletter, inklusive Testlauf mit echten Daten." },
                { task: "Produktimport und Datenpflege", time: "2 bis 6 Stunden", cost: "220 bis 660 €", note: "Produkte, Varianten, Bilder und Metafelder aus CSV oder Altsystem sauber uebernehmen." },
                { task: "Fehlersuche und Altlasten", time: "ab 1 Stunde", cost: "ab 110 €", note: "Reste alter Apps, doppelte Skripte, kaputte Weiterleitungen. Erst Ursache finden, dann reparieren." },
                { task: "Ladezeit verbessern", time: "2 bis 5 Stunden", cost: "220 bis 550 €", note: "Bilder, Skripte und Apps pruefen, unnötige Last entfernen, Wirkung messen statt raten." },
                { task: "Laufende Betreuung", time: "2 bis 4 Stunden pro Monat", cost: "220 bis 440 € im Monat", note: "Fuer alles, was aufläuft. Ohne Vertragsbindung, Abrechnung nach tatsaechlichem Aufwand." },
              ].map((e) => (
                <div key={e.task} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{e.task}</h3>
                  <p className="text-signal mt-2 text-lg font-bold">{e.cost}</p>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{e.time}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{e.note}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Alle Angaben netto, Stundensatz 110 €. Größere Vorhaben laufen als Festpreis,
              der schriftlich feststeht, bevor entwickelt wird.
            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Ehrliche Beratung
            </p>
            <h2 className="mt-4 text-3xl font-bold md:text-4xl">
              Erst prüfen, ob es eine App <span className="text-signal">schon löst.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Viele Entwicklungsanfragen sind in Wahrheit keine. Wenn eine gepflegte App Ihre
              Aufgabe fuer wenig Geld im Monat loest, empfehle ich die App und richte sie ein,
              statt Ihnen eine Eigenentwicklung zu verkaufen. Eigenentwicklung lohnt sich, wenn
              die App-Landschaft Ihren Fall nicht abbildet oder laufende App-Kosten den
              Einmalfreis uebersteigen. Diese Rechnung lege ich Ihnen offen.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Und wenn eine Aufgabe ausserhalb meines Schwerpunkts liegt, etwa native Apps oder
              komplexe ERP-Projekte, sage ich das und nenne Ihnen, wer es besser kann.
            </p>
          </div>
        </section>

        <CaseStrip
          slugs={["maskabel", "deinlunu"]}
          title="Entwicklungsarbeit aus echten Shops"
          intro="Bei MASKABEL ging es um Kabelfinder, Preis- und Mengenlogik und schnelle Eingaben auf dem Handy. Bei Lunu um Produktseite, Bundle-Logik und kürzere mobile Kaufwege."
        />

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">So läuft es ab</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { no: "01", title: "Aufgabe und Zugang", text: "Sie schildern die Aufgabe, ich sehe mir Shop und Theme an und nenne Ihnen den zu erwartenden Aufwand." },
                { no: "02", title: "Umsetzung", text: "Gearbeitet wird in einer Kopie Ihres Themes oder in einem Testshop. Der laufende Shop bleibt unberührt." },
                { no: "03", title: "Freigabe und Livegang", text: "Sie sehen das Ergebnis, wir gehen es durch, danach veröffentliche ich. Abrechnung nach tatsächlichem Aufwand." },
              ].map((s) => (
                <div key={s.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-3xl font-bold">{s.no}</span>
                  <h3 className="mt-3 text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Geht es um eine konkrete Theme-Anpassung?{" "}
              <Link to="/shopify-theme-anpassung" className="text-primary underline underline-offset-4">
                Theme anpassen lassen
              </Link>
              . Noch kein Shop vorhanden?{" "}
              <Link to="/shopify-shop-erstellen-lassen" className="text-primary underline underline-offset-4">
                Shop erstellen lassen
              </Link>
              . Mehr Hintergrund im{" "}
              <Link to="/ratgeber/shopify-entwickler" className="text-primary underline underline-offset-4">
                Ratgeber Shopify Entwickler
              </Link>
              .
            </p>
          </div>
        </section>

        <section id="kontakt" className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Schicken Sie mir <span className="text-signal">Ihre Aufgabe</span>.
              </h2>
              <p className="mt-5 text-muted-foreground">
                Eine kurze Beschreibung reicht. Sie bekommen innerhalb von 48 Stunden eine
                Einschätzung, den empfohlenen nächsten Schritt und, soweit möglich, einen
                groben Aufwand. Kostenlos und unverbindlich.
              </p>
            </div>
            <LeadForm
              source="lp-shopify-entwickler"
              compact={false}
              showServices
              defaultService="Anpassungen & Entwicklung"
              headline="Entwicklungsaufwand einschätzen lassen"
              cta="Kostenlose Einschätzung erhalten"
              messagePlaceholder="Welche Entwicklungsaufgabe steht an, welches Theme nutzen Sie?"
            />
          </div>
        </section>
      </main>

      <StickyCta targetId="anfrage" />
      <GuideLinks
        links={[
        { to: "/shopify-app-entwicklung", label: "Shopify App Entwicklung", text: "Custom App, Schnittstelle oder Theme-Anpassung." },
        { to: "/blog/shopify-apps-auswaehlen", label: "Shopify Apps auswaehlen", text: "Wann eine App sich lohnt und wann nicht." },
        { to: "/shopify-theme-anpassung", label: "Theme-Anpassung", text: "Aenderungen direkt im Theme statt zusaetzlicher App." },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
